import React, { useEffect, useRef, useState } from "react";
import SideBar from "../Components/SideBar";
import Alert from "../Components/Alert";
import "./bodyContainer.css";
import PropTypes from "prop-types";
import SocialMedia from "../Components/socialMedia";
import {
  getCurrentUser,
  updateProfile,
  deleteProfilePic,
  uploadProfilePic,
} from "../Services/usersApi";
import { useTranslation } from "react-i18next";
import PROFILEIMAGE from "../assets/ProfilePicture.png";
import { ClipLoader } from "react-spinners";

function ChangeImage({ showAlert, setSelectedImage }) {
  const { t } = useTranslation();
  const inputFile = useRef(null);

  const handleChangeImage = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setSelectedImage(selectedFile); // Set to parent state

    const form = new FormData();
    form.append("image", selectedFile);

    uploadProfilePic(form)
      .then(() => {
        showAlert(t("profile_picture_updated"), "success");
        window.location.reload(); // OR update profile image in state instead of reloading
      })
      .catch(() => {
        showAlert(t("could_not_upload_profile_picture"), "danger");
      });
  };

  return (
    <>
      <button
        onClick={() => inputFile.current.click()}
        className="bg-gradient-to-r font-md text-gray-300 from-gray-700 to-slate-600 shadow-lg p-2 text-white rounded-lg w-full hover:scale-105 transition duration-300 ease-in-out w-full sm:w-auto"
      >
        {t("change")}
      </button>
      <input
        onChange={handleChangeImage}
        className="sr-only"
        type="file"
        ref={inputFile}
        style={{ display: "none" }}
      />
    </>
  );
}

ChangeImage.propTypes = {
  showAlert: PropTypes.func.isRequired,
  setSelectedImage: PropTypes.func.isRequired,
};

function ProfilePage() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({});
  const [formValid, setFormValid] = useState(true);
  const [showError, setShowError] = useState({});
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    message: "",
    type: null,
  });
  const [pageLoading, setPageLoading] = useState(true);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [socialMediaData, setSocialMediaData] = useState([]);
  const [errorMessage, setErrorMessage] = useState({});
  const [imgLoading, setImgLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const accessToken =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    const refreshToken =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");

    if (!accessToken && !refreshToken) {
      window.location.href = "/login";
      return;
    }

    document.title = process.env.REACT_APP_REGION_NAME;

    getCurrentUser()
      .then((res) => {
        setProfile(res.data);
        if (!res.data.metadata?.socialMedia) {
          setSocialMediaData([]);
        }
        setPageLoading(false);
      })
      .catch((error) => {
        setPageLoading(false);
        setAlertInfo({
          show: true,
          message: t("failedToFetchData"),
          type: "danger",
        });
        console.error("Error fetching profile:", error);
      });
  }, [t]);

  useEffect(() => {
    const isValid = !Object.values(showError).some((e) => e);
    setFormValid(isValid);
  }, [showError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "description" || name === "website") {
      setProfile((prev) => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [name]: value,
        },
      }));
      return;
    }

    if (name === "name") {
      if (!value.trim()) {
        setShowError((prev) => ({ ...prev, name: true }));
        setErrorMessage((prev) => ({
          ...prev,
          name: t("this_field_cannot_be_empty"),
        }));
      } else {
        const updatedProfile = { ...profile, [name]: value };
        setProfile(updatedProfile);
        setShowError((prev) => ({ ...prev, name: false }));
        setErrorMessage((prev) => ({ ...prev, name: "" }));
      }
    }

    if (name === "email") {
      return;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValid = re.test(value);
      if (isValid) {
        const updatedProfile = { ...profile, [name]: value };
        setProfile(updatedProfile);
      }
      setShowError((prev) => ({ ...prev, email: !isValid }));
    }
    if (name === "phoneNumber") {
      const re = /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s/0-9]{0,10}$/g;
      const isValid = re.test(value.trim());

      const updatedProfile = { ...profile, [name]: value };
      setProfile(updatedProfile);

      if (!value.trim()) {
        setShowError((prev) => ({ ...prev, phoneNumber: true }));
        setErrorMessage((prev) => ({
          ...prev,
          phoneNumber: t("this_field_cannot_be_empty"),
        }));
      } else if (!isValid) {
        setShowError((prev) => ({ ...prev, phoneNumber: true }));
        setErrorMessage((prev) => ({
          ...prev,
          phoneNumber: t("invalid_phone_number"),
        }));
      } else {
        setShowError((prev) => ({ ...prev, phoneNumber: false }));
        setErrorMessage((prev) => ({ ...prev, phoneNumber: "" }));
      }
    }
  };
  const updateChanges = () => {
    if (!formValid) {
      setAlertInfo({ show: true, message: t("invalidData"), type: "danger" });
      return;
    }

    setUpdatingProfile(true);
    const { image, metadata, ...profileCleaned } = profile;

    const updatedProfile = {
      ...profileCleaned,
      image,
      metadata: {
        ...metadata,
        description: profile.metadata?.description || "",
        website: profile.metadata?.website || "",
        socialMedia: socialMediaData,
      },
    };

    updateProfile(updatedProfile)
      .then(() => {
        setUpdatingProfile(false);
        setAlertInfo({
          show: true,
          message: t("your_changes_were_saved_successfully"),
          type: "success",
        });
      })
      .catch((err) => {
        setUpdatingProfile(false);
        setAlertInfo({
          show: true,
          message: err?.response?.data?.message || t("changesNotSaved"),
          type: "danger",
        });
      });
  };

  const showAlert = (message, type) => {
    setAlertInfo({ show: true, message, type });
    setTimeout(() => {
      setAlertInfo({ show: false, message: "", type: null });
    }, 2000);
  };

  useEffect(() => {
    if (alertInfo.show) {
      const timer = setTimeout(() => {
        setAlertInfo({ show: false, message: "", type: null });
      }, 2000);
      return () => clearTimeout(timer); // cleanup
    }
  }, [alertInfo.show]);

  return (
    <div className="bg-gray-900 body-font relative min-h-screen">
      <SideBar />
      {pageLoading ? (
        <div className="flex h-screen justify-center items-center">
          <div className="loader"></div>
        </div>
      ) : (
        <div>
          <div className="container w-auto px-5 py-2">
            <div className="bg-white mt-4 p-6">
              <h2
                className="text-slate-800 text-lg mb-4 font-medium title-font"
                style={{
                  fontFamily:
                    "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
                }}
              >
                {t("account")}
                <div className="my-4 bg-gray-600 h-[1px]" />
              </h2>

              <div className="py-2 grid grid-cols-1 md:grid-cols-2">
                <div className="mt-1 px-2">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("name")} *
                  </label>
                  <input
                    name="name"
                    onChange={handleInputChange}
                    className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                    value={profile.name || ""}
                  />
                  <div
                    className="mt-2 text-sm text-red-600"
                    style={{
                      visibility: showError.name ? "visible" : "hidden",
                    }}
                  >
                    {errorMessage.name}
                  </div>
                </div>

                <div className="mt-1 px-2">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("username")}
                  </label>
                  <input
                    type="text"
                    name="username"
                    id="username"
                    className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                    placeholder={t("pleaseEnterUsername")}
                    value={profile.username || ""}
                    onChange={handleInputChange}
                    style={{
                      fontFamily:
                        "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
                    }}
                  />
                </div>

                <div className="mt-1 px-2">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("email")} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profile.email || ""}
                    onChange={handleInputChange}
                    className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                  />
                  <div
                    className="mt-2 text-sm text-red-600"
                    style={{
                      visibility: showError.email ? "visible" : "hidden",
                    }}
                  >
                    {errorMessage.email}
                  </div>
                </div>

                <div className="mt-1 px-2">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("phoneNumber")}*
                  </label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={profile.phoneNumber || ""}
                    onChange={handleInputChange}
                    className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                  />
                  <div
                    className="mt-2 text-sm text-red-600"
                    style={{
                      visibility: showError.phoneNumber ? "visible" : "hidden",
                    }}
                  >
                    {errorMessage.phoneNumber}
                  </div>
                </div>
              </div>

              <div className="py-3 grid grid-cols-1">
                <div className="mt-1 px-2">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("profileImage")}
                  </label>
                  <div className="py-2 md:flex md:space-x-4 flex-raw space-y-2">
                    <div className="flex md:flex-col justify-center items-start">
                      <div className="relative w-20 h-20">
                        {imgLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-full animate-pulse">
                            <ClipLoader color="#36D7B7" size={40} />
                          </div>
                        )}
                        <img
                          className="rounded-full h-20 w-20 object-cover"
                          src={
                            selectedImage
                              ? URL.createObjectURL(selectedImage)
                              : profile.image || PROFILEIMAGE
                          }
                          alt="profile"
                          onLoad={() => setImgLoading(false)}
                          onError={() => setImgLoading(false)}
                          style={{ display: imgLoading ? "none" : "block" }}
                        />
                      </div>
                    </div>
                    <div className="flex md:flex-col justify-center items-start">
                      <ChangeImage
                        showAlert={showAlert}
                        setSelectedImage={setSelectedImage}
                      />
                    </div>
                    <div className="flex md:flex-col justify-center md:items-center item-center">
                      <button
                        onClick={() => {
                          deleteProfilePic()
                            .then(() => {
                              showAlert(
                                t("profile_picture_removed"),
                                "success"
                              );
                              window.location.reload();
                            })
                            .catch(() => {
                              showAlert(
                                t("could_not_remove_profile_picture"),
                                "danger"
                              );
                            });
                        }}
                        className="bg-gradient-to-r font-md text-gray-300 from-gray-700 to-slate-600 shadow-lg p-2 text-white rounded-lg w-full hover:scale-105 transition duration-300 ease-in-out"
                      >
                        {t("remove")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="py-3 grid grid-cols-1">
                <div className="mt-1 px-2">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("description")}
                  </label>
                  <textarea
                    name="description"
                    onChange={handleInputChange}
                    className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                    value={profile.metadata?.description || ""}
                  />
                </div>

                <div className="mt-1 px-2">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("website")}
                  </label>
                  <input
                    name="website"
                    onChange={handleInputChange}
                    className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                    value={profile.metadata?.website || ""}
                  />
                </div>
              </div>
            </div>
          </div>

          <SocialMedia setSocialMedia={(data) => setSocialMediaData(data)} />

          <div className="container w-auto px-5 py-2 bg-gray-900">
            <div className="bg-white mt-4 p-6">
              <button
                className="bg-gradient-to-r font-md text-gray-300 from-gray-700 to-slate-600 shadow-lg p-2 text-white rounded-lg w-full 
                hover:scale-[1.01] sm:hover:scale-[1.015] md:hover:scale-[1.02] lg:hover:scale-[1.025] hover:from-gray-700 hover:to-slate-600 transition duration-300 ease-in-out"
                onClick={updateChanges}
                disabled={!formValid || updatingProfile}
              >
                {t("saveChanges")}
                {updatingProfile && <span className="loader inline ml-2" />}
              </button>
              {alertInfo.show && (
                <div className="py-2 mt-1 px-2">
                  <Alert type={alertInfo.type} message={alertInfo.message} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
